import { useEffect, useRef, useState } from 'preact/hooks';
import dayjs from 'dayjs';
import { rpx } from 'client/lib/rpx-client';
import { isNetworkError } from 'client/lib/ajax';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { IcoCheck } from '@components/icons';
import { showToast } from '@components/toaster';
import type { CheckinSummary } from 'server/dbx/checkin-summaries';
import type { SummaryWindow } from 'shared/checkin-weeks';
import { useIntl } from 'shared/intl/use-intl';
import { MAX_CHECKIN_SUMMARY_LENGTH as MAX_SUMMARY_LENGTH } from 'shared/checkin-feedback';
import { addDays } from 'shared/checkin-weeks';
import { fmtShortDate } from 'shared/tracker-people';
import {
  copyTrackerText,
  TRACKER_BOX_CLASS,
  type TrackerGlance,
} from '@course/components/tracker/glance';

const AUTOSAVE_DEBOUNCE_MS = 1500;

type Props = {
  courseId: UUID;
  window: SummaryWindow;
  localToday: string;
  current?: CheckinSummary;
  onSaved(summary: CheckinSummary): void;
  /** The tracker's numbers for this summary's week. */
  tracker?: TrackerGlance['summaryWeek'];
};

function longDay(date: string) {
  return dayjs(date).format('dddd, MMM D');
}

/** The summary week's tracker numbers, with the Tracker tab's Copy for Claude text. */
function TrackerWeekLine({
  tracker,
  weekNumber,
}: {
  tracker: NonNullable<TrackerGlance['summaryWeek']>;
  weekNumber: number;
}) {
  const intl = useIntl();
  const heading = tracker.isSprint
    ? intl('Immersion sprint so far: {line:string}', { line: tracker.line })
    : intl('Week {week:number} ({from:string}–{to:string}): {line:string}', {
        week: weekNumber,
        from: fmtShortDate(tracker.weekStart),
        to: fmtShortDate(addDays(tracker.weekStart, 6)),
        line: tracker.line,
      });

  function copy() {
    copyTrackerText(tracker.copyText, intl('Copied for Claude'), {
      title: intl('Couldn’t copy'),
      message: intl('Your browser blocked the clipboard. Try again.'),
    });
  }

  return (
    <div class={TRACKER_BOX_CLASS}>
      <span class="flex flex-col min-w-0">
        <span class="text-gray-800 dark:text-gray-200">{heading}</span>
        {tracker.betLines.map((line) => (
          <span key={line} class="text-gray-700 dark:text-gray-300">
            {line}
          </span>
        ))}
        <span class="text-gray-500 dark:text-gray-400">{tracker.scoreboardLine}</span>
      </span>
      <BtnSecondary class="min-h-11 shrink-0 bg-white dark:bg-gray-800" onClick={copy}>
        {intl('Copy for Claude')}
      </BtnSecondary>
    </div>
  );
}

/** "Weekly summary · Week 1" or "Sprint summary". */
export function useSummaryTitle() {
  const intl = useIntl();
  return (window: Pick<SummaryWindow, 'isSprint' | 'weekNumber'>) =>
    window.isSprint
      ? intl('Sprint summary')
      : intl('Weekly summary · Week {week:number}', { week: window.weekNumber });
}

/**
 * The weekly summary box on the Standups page. Students paste the summary
 * their weekly review conversation writes; it opens Friday and is due by the
 * end of Sunday, and in the last week it collects the Sprint summary.
 */
export function WeeklySummaryCard({
  courseId,
  window,
  localToday,
  current,
  onSaved,
  tracker,
}: Props) {
  const intl = useIntl();
  const title = useSummaryTitle()(window);
  const [summary, setSummary] = useState(current);
  const [text, setText] = useState(current?.body || '');
  const [isEditing, setIsEditing] = useState(!current || current.isDraft);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const savedTextRef = useRef(text);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const isLate = !!window.dueOn && localToday > window.dueOn;
  const over = text.length > MAX_SUMMARY_LENGTH;

  async function saveDraft() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    // Once sent, changes are saved only by Update summary, so Cancel
    // really leaves the coach's copy as it was.
    if (
      summary?.submittedAt ||
      // Nothing typed and nothing saved: nothing to do. Clearing a saved
      // draft still saves, so the old text doesn't come back.
      (!text.trim() && !savedTextRef.current.trim()) ||
      over ||
      isSending ||
      text === savedTextRef.current
    ) {
      return;
    }
    savedTextRef.current = text;
    try {
      const saved = await rpx.checkins.saveSummaryDraft({
        courseId,
        weekStart: window.weekStart,
        body: text,
      });
      if (saved) {
        setSummary(saved);
        onSaved(saved);
      }
    } catch {
      // Silent, like standup autosave: the text is still in the box, and
      // Send reports real errors.
    }
  }

  useEffect(() => {
    if (text === savedTextRef.current) {
      return;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(saveDraft, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [text]);

  async function send() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    const wasSent = !!summary?.submittedAt;
    setIsSending(true);
    setError('');
    try {
      const saved = await rpx.checkins.submitSummary({
        courseId,
        weekStart: window.weekStart,
        body: text,
      });
      savedTextRef.current = text;
      setSummary(saved);
      onSaved(saved);
      setIsEditing(false);
      showToast({
        type: 'ok',
        title: wasSent ? intl('Summary updated') : intl('Summary sent'),
        message: wasSent
          ? intl('Your coach sees the new version.')
          : intl('Your coach can read it in the course now.'),
      });
    } catch (err) {
      setError(
        isNetworkError(err as Error)
          ? intl(
              'Your summary didn’t send because the connection dropped. Your text is still here, so send it again.',
            )
          : (err as Error).message,
      );
    } finally {
      setIsSending(false);
    }
  }

  const due = window.isSprint
    ? intl('Due before your coaching call')
    : isLate
      ? intl('Was due {due:string}. You can still send it until {closes:string}.', {
          due: longDay(window.dueOn!),
          closes: longDay(window.closesOn),
        })
      : intl('Due by the end of {due:string}', { due: longDay(window.dueOn!) });

  if (summary?.submittedAt && !isEditing) {
    return (
      <section class="flex items-center justify-between gap-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-6">
        <div class="flex items-center gap-3">
          <span class="flex items-center justify-center size-8 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 shrink-0">
            <IcoCheck class="size-4" />
          </span>
          <div class="flex flex-col">
            <h2 class="font-medium text-gray-900 dark:text-gray-100">
              {window.isSprint
                ? intl('Sprint summary sent')
                : intl('Weekly summary for week {week:number} sent', { week: window.weekNumber })}
            </h2>
            <span class="text-sm text-gray-500">
              {intl('Sent {when:string} · you can edit it until {closes:string}', {
                when: dayjs(summary.submittedAt).format('ddd [at] h:mm A'),
                closes: longDay(window.closesOn),
              })}
            </span>
          </div>
        </div>
        <BtnSecondary onClick={() => setIsEditing(true)}>{intl('Edit')}</BtnSecondary>
      </section>
    );
  }

  return (
    <section class="flex flex-col gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-6">
      <header class="flex flex-col">
        <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">{title}</h2>
        <span class={`text-sm ${isLate ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500'}`}>
          {due}
        </span>
      </header>
      {tracker && tracker.weekStart === window.weekStart && (
        <TrackerWeekLine tracker={tracker} weekNumber={window.weekNumber} />
      )}
      <label class="text-sm text-gray-700 dark:text-gray-300" for="weekly-summary">
        {window.isSprint
          ? intl('Paste the Sprint Summary from your review conversation')
          : intl('Paste the coach summary from your weekly review')}
      </label>
      <textarea
        id="weekly-summary"
        class="ruz-input"
        rows={8}
        value={text}
        disabled={isSending}
        placeholder={
          window.isSprint
            ? ''
            : 'Built: …\nState: …\nStuck: …\nNext: …\nWhere I could use a nudge: …'
        }
        onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
        onBlur={saveDraft}
      />
      {error && (
        <p role="alert" class="text-sm text-red-700 dark:text-red-400">
          {error}
        </p>
      )}
      <footer class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <BtnPrimary onClick={send} isLoading={isSending} disabled={!text.trim() || over}>
            {summary?.submittedAt ? intl('Update summary') : intl('Send to coach')}
          </BtnPrimary>
          {summary?.submittedAt && (
            <BtnSecondary
              disabled={isSending}
              onClick={() => {
                setText(summary.body);
                savedTextRef.current = summary.body;
                setIsEditing(false);
                setError('');
              }}
            >
              {intl('Cancel')}
            </BtnSecondary>
          )}
        </div>
        <span class={`text-xs ${over ? 'text-red-700 dark:text-red-400' : 'text-gray-500'}`}>
          {intl('{count:string} of {max:string} characters', {
            count: text.length.toLocaleString(),
            max: MAX_SUMMARY_LENGTH.toLocaleString(),
          })}
        </span>
      </footer>
    </section>
  );
}
